import React from 'react';
import styled from 'styled-components';

import { ProcessedFlightHistory, Status } from '../../types/flight';
import { DateTimeRow } from './DateTimeRow';
import { getStructuredDates, TimeRowData, LabeledDateTime } from './flightDataUtils';
import { getStatusTheme } from './ColorStatus';
import { renderDuration } from '../../lib/date';
import { iataOrFallBackToLabeledFS } from './FlightViewBody';

export const emptyFieldCharacter = '--';

interface FlightSummaryProps {
  flightData: ProcessedFlightHistory | null;
}

enum FlightStatus {
  REDIRECTED = 'REDIRECTED',
  DIVERTED = 'DIVERTED',
  CANCELLED = 'CANCELLED',
  UNKNOWN = 'UNKNOWN',
}

export const RowWrapper = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  text-align: center;
  justify-content: center;
`;

const DirectionHeader = styled.div<{ color: string }>`
  color: white;
  font-family: Arial, Helvetica, sans-serif;
  background-color: ${props => props.color};
  justify-content: center;
  border-bottom: 1px solid white;
  font-weight: 400;
  font-size: 22px;
  padding: 10px 10px;
`;

const DirectionBody = styled.div`
  text-align: center;
`;

const LargeBold = styled.div`
  font-weight: 400;
  font-size: 22px;
  padding: 10px 10px;
`;

const SmallBold = styled.div`
  font-size: 12px;
  font-weight: bold;
`;

const SmallNonBoldDiv = styled.div`
  font-size: 10px;
  font-weight: normal;
`;

interface PaneProps {
  aspect: string;
  size?: string;
  color: string;
}

const Pane = styled.div<PaneProps>`
  flex-basis: ${props => {
    const is21or42 = ['2:1', '4:2'].includes(props.aspect);
    const is41 = props.aspect === '4:1';
    return is21or42 ? '49%' : is41 ? '24%' : '99%';
  }};
  flex-direction: column;
  color: #ffffff;
  font-family: Arial, Helvetica, sans-serif;
  padding-bottom: 15px;
  background-color: ${props => props.color};
  ${props => (props.size === 'half' ? 'min-width: 320px;\nmax-width: 500px' : '')}
`;

export const Vr = styled.div`
  border-right: 1px solid white;
`;

const calculateDelay = (gateOrRunwayDates: TimeRowData, status: Status | null): string => {
  const scheduled = gateOrRunwayDates?.scheduled?.dateTime;
  const second = gateOrRunwayDates?.second?.dateTime;
  if (
    scheduled && second &&
    scheduled.zone?.name === second.zone?.name &&
    ![FlightStatus.REDIRECTED, FlightStatus.DIVERTED, FlightStatus.CANCELLED, FlightStatus.UNKNOWN, undefined].includes(status as FlightStatus | undefined)
  ) {
    return (
      renderDuration(scheduled, second, {
        units: ['days', 'hours', 'minutes'],
      }) || emptyFieldCharacter
    );
  } else {
    return emptyFieldCharacter;
  }
};

interface LabeledDuration {
  label: string;
  duration: string;
}

const calculateFlightDuration = (from: LabeledDateTime, to: LabeledDateTime): LabeledDuration => {
  if (from && to) {
    return {
      label: [from.label, to.label].includes('Estimated') ? 'Estimated' : from.label,
      duration:
        from.dateTime && to.dateTime
          ? renderDuration(from.dateTime, to.dateTime, {
              units: ['hours', 'minutes'],
              unitDisplay: 'narrow',
            }) || emptyFieldCharacter
          : emptyFieldCharacter,
    };
  } else {
    return {
      label: [from.label, to.label].includes('Scheduled') ? 'Scheduled' : 'Actual',
      duration: emptyFieldCharacter,
    };
  }
};

export const FlightSummary: React.FC<FlightSummaryProps> = ({ flightData }) => {
  const dateTimeData = getStructuredDates(flightData);

  // non-date departure fields
  const departureAirportDisplayCode = iataOrFallBackToLabeledFS(flightData?.departureDetails);
  const departureAirportName = flightData?.departureDetails?.displayName || emptyFieldCharacter;
  const departureTerminal = flightData?.departureTerminal || emptyFieldCharacter;
  const departureGate = flightData?.departureGate || emptyFieldCharacter;

  // departure-side other
  const scheduledCraftType = flightData?.scheduledEquipmentDetails?.name || emptyFieldCharacter;
  const scheduledEquipmentIata = flightData?.scheduledEquipmentDetails?.iata || emptyFieldCharacter;
  const actualCraftType = flightData?.actualEquipmentDetails?.name || emptyFieldCharacter;
  const actualEquipmentIata = flightData?.actualEquipmentDetails?.iata || emptyFieldCharacter;
  const hasBothScheduledGate = !!(
    dateTimeData?.departure?.gate?.scheduled && dateTimeData?.arrival?.gate?.scheduled
  );
  const scheduledFlightDuration = hasBothScheduledGate
    ? calculateFlightDuration(
        dateTimeData.departure.gate.scheduled,
        dateTimeData.arrival.gate.scheduled,
      )
    : calculateFlightDuration(
        dateTimeData?.departure?.runway?.scheduled,
        dateTimeData?.arrival?.runway?.scheduled,
      );

  // non-date arrival fields
  const arrivalAirportDisplayCode = iataOrFallBackToLabeledFS(flightData?.arrivalDetails);
  const arrivalAirportName = flightData?.arrivalDetails?.name || emptyFieldCharacter;
  const arrivalTerminal = flightData?.arrivalTerminal || emptyFieldCharacter;
  const arrivalGate = flightData?.arrivalGate || emptyFieldCharacter;
  const baggageClaim = flightData?.baggage || emptyFieldCharacter;

  // arrival-side other
  const tailNumber = flightData?.tailNumber || emptyFieldCharacter;
  const hasBothSecondGate = !!(
    dateTimeData?.departure?.gate?.second && dateTimeData?.arrival?.gate?.second
  );
  const secondFlightDuration = hasBothSecondGate
    ? calculateFlightDuration(
        dateTimeData.departure.gate.second,
        dateTimeData.arrival.gate.second,
      )
    : calculateFlightDuration(
        dateTimeData?.departure?.runway?.second,
        dateTimeData?.arrival?.runway?.second,
      );

  const statusColoring = getStatusTheme(flightData?.status ?? '');

  return (
    <>
      <RowWrapper>
        <Pane aspect="2:1" size="half" color={statusColoring.body}>
          <DirectionHeader color={statusColoring.header}>Departure</DirectionHeader>
          <DirectionBody>
            <LargeBold>{departureAirportDisplayCode}</LargeBold>
            <div>
              <SmallBold>{departureAirportName}</SmallBold>
              <SmallBold>Flight Gate Times</SmallBold>
            </div>
            <DateTimeRow dateTimeRowData={dateTimeData.departure.gate} />
            <div>
              <SmallNonBoldDiv>
                Gate Departure Delay:{' '}
                {calculateDelay(dateTimeData.departure.gate, flightData?.status ?? null)}
              </SmallNonBoldDiv>
              <hr />
              <SmallBold>Flight Runway Times</SmallBold>
            </div>
            <DateTimeRow dateTimeRowData={dateTimeData.departure.runway} />
            <div>
              <SmallNonBoldDiv>
                Runway Departure Delay:{' '}
                {calculateDelay(dateTimeData.departure.runway, flightData?.status ?? null)}
              </SmallNonBoldDiv>
              <hr />
            </div>
            <RowWrapper>
              <Pane aspect="4:1" color={statusColoring.body}>
                <SmallBold>Terminal</SmallBold>
                <LargeBold>{departureTerminal}</LargeBold>
              </Pane>
              <Vr />
              <Pane aspect="4:1" color={statusColoring.body}>
                <SmallBold>Gate</SmallBold>
                <LargeBold>{departureGate}</LargeBold>
              </Pane>
              <Vr />
              <Pane aspect="4:2" color={statusColoring.body}></Pane>
            </RowWrapper>
            <div>
              <hr />
            </div>
            <RowWrapper>
              <Pane aspect="4:1" color={statusColoring.body}>
                <SmallBold>
                  Scheduled
                  <br />
                  Craft Type
                </SmallBold>
                <SmallNonBoldDiv>{scheduledCraftType}</SmallNonBoldDiv>
                <LargeBold>{scheduledEquipmentIata}</LargeBold>
              </Pane>
              <Vr />
              <Pane aspect="4:1" color={statusColoring.body}>
                <SmallBold>
                  Actual
                  <br />
                  Craft Type
                </SmallBold>
                <SmallNonBoldDiv>{actualCraftType}</SmallNonBoldDiv>
                <LargeBold>{actualEquipmentIata}</LargeBold>
              </Pane>
              <Pane aspect="2:1" color={statusColoring.body}>
                <SmallBold>Flight Time</SmallBold>
                <SmallNonBoldDiv>{scheduledFlightDuration.label}</SmallNonBoldDiv>
                <LargeBold>{scheduledFlightDuration.duration}</LargeBold>
              </Pane>
            </RowWrapper>
          </DirectionBody>
        </Pane>
        <Pane aspect="2:1" size="half" color={statusColoring.body}>
          <DirectionHeader color={statusColoring.header}>Arrival</DirectionHeader>
          <DirectionBody>
            <LargeBold>{arrivalAirportDisplayCode}</LargeBold>
            <div>
              <SmallBold>{arrivalAirportName}</SmallBold>
              <SmallBold>Flight Gate Times</SmallBold>
            </div>
            <DateTimeRow dateTimeRowData={dateTimeData.arrival.gate} />
            <div>
              <SmallNonBoldDiv>
                Gate Arrival Delay:{' '}
                {calculateDelay(dateTimeData.arrival.gate, flightData?.status ?? null)}
              </SmallNonBoldDiv>
              <hr />
              <SmallBold>Flight Runway Times</SmallBold>
            </div>
            <DateTimeRow dateTimeRowData={dateTimeData.arrival.runway} />
            <div>
              <SmallNonBoldDiv>
                Runway Arrival Delay:{' '}
                {calculateDelay(dateTimeData.arrival.runway, flightData?.status ?? null)}
              </SmallNonBoldDiv>
              <hr />
            </div>
            <RowWrapper>
              <Pane aspect="4:1" color={statusColoring.body}>
                <SmallBold>Terminal</SmallBold>
                <LargeBold>{arrivalTerminal}</LargeBold>
              </Pane>
              <Vr />
              <Pane aspect="4:1" color={statusColoring.body}>
                <SmallBold>Gate</SmallBold>
                <LargeBold>{arrivalGate}</LargeBold>
              </Pane>
              <Vr />
              <Pane aspect="4:2" color={statusColoring.body}>
                <SmallBold>Baggage Claim</SmallBold>
                <LargeBold>{baggageClaim}</LargeBold>
              </Pane>
            </RowWrapper>
            <div>
              <hr />
            </div>
            <RowWrapper>
              <Pane aspect="2:1" color={statusColoring.body}>
                <SmallBold>Tail Number</SmallBold>
                <LargeBold>{tailNumber}</LargeBold>
              </Pane>
              <Pane aspect="2:1" color={statusColoring.body}>
                <SmallBold>Flight Time</SmallBold>
                <SmallNonBoldDiv>{secondFlightDuration.label}</SmallNonBoldDiv>
                <LargeBold>{secondFlightDuration.duration}</LargeBold>
              </Pane>
            </RowWrapper>
          </DirectionBody>
        </Pane>
      </RowWrapper>
    </>
  );
};